import type { MatchTelemetryRow } from './Telemetry.js';

// V3 daily challenge engine. Three challenges are selected per UTC calendar day
// using the date string as a deterministic seed — same day always shows the same
// three challenges for every player. Progress is computed from that day's
// telemetry rows (rows where recordedAt starts with todayUtc).

export type ChallengeId =
  | 'win-any'
  | 'play-3'
  | 'play-5'
  | 'win-vanguard'
  | 'win-saboteur'
  | 'win-bulwark'
  | 'build-8-squads'
  | 'win-fast';

export interface ChallengeDefinition {
  id: ChallengeId;
  label: string;
  goal: number;
}

export interface ChallengeProgress {
  def: ChallengeDefinition;
  current: number;
  done: boolean;
}

const CHALLENGE_POOL: ChallengeDefinition[] = [
  { id: 'win-any', label: 'Win a match', goal: 1 },
  { id: 'play-3', label: 'Play 3 matches', goal: 3 },
  { id: 'play-5', label: 'Play 5 matches', goal: 5 },
  { id: 'win-vanguard', label: 'Win with Vanguard doctrine', goal: 1 },
  { id: 'win-saboteur', label: 'Win with Saboteur doctrine', goal: 1 },
  { id: 'win-bulwark', label: 'Win with Bulwark doctrine', goal: 1 },
  { id: 'build-8-squads', label: 'Build 8+ squads in one match', goal: 1 },
  { id: 'win-fast', label: 'Win in under 5 minutes', goal: 1 },
];

export function getTodayUtcDateStr(): string {
  return new Date().toISOString().slice(0, 10); // YYYY-MM-DD
}

// Deterministic 3-item selection from CHALLENGE_POOL keyed to UTC date string.
// Uses a seeded LCG shuffle so the same date always yields the same three
// challenges regardless of client or time of day.
export function getDailyChallenges(utcDateStr: string): ChallengeDefinition[] {
  let seed = 0;
  for (let i = 0; i < utcDateStr.length; i++) {
    seed = ((seed * 31 + utcDateStr.charCodeAt(i)) >>> 0);
  }
  const pool = [...CHALLENGE_POOL];
  for (let i = pool.length - 1; i > 0; i--) {
    seed = ((seed * 1664525 + 1013904223) >>> 0);
    const j = seed % (i + 1);
    const tmp = pool[i]!;
    pool[i] = pool[j]!;
    pool[j] = tmp;
  }
  return pool.slice(0, 3);
}

export function getDailyChallengeProgress(
  challenges: ChallengeDefinition[],
  rows: readonly MatchTelemetryRow[],
  todayUtc: string,
): ChallengeProgress[] {
  const todayRows = rows.filter((r) => r.recordedAt.startsWith(todayUtc));
  return challenges.map((def) => {
    const current = measureProgress(def, todayRows);
    return { def, current, done: current >= def.goal };
  });
}

// V2 Tempo hot-fix: stale localStorage rows may be missing `outcome` /
// `squadsBuilt` (only 4 fields are validated by Telemetry.isPlausibleRow).
// Coerce defensively so the daily-challenges read never crashes.
function viewerWonRow(r: MatchTelemetryRow): boolean {
  const o = (r as { outcome?: { winner?: unknown } }).outcome;
  return o !== undefined && o !== null && o.winner === 0;
}
function viewerDoctrine(r: MatchTelemetryRow): string | null {
  if (!Array.isArray(r.doctrines)) return null;
  const v = r.doctrines[0];
  return typeof v === 'string' ? v : null;
}
function viewerSquadsBuilt(r: MatchTelemetryRow): number {
  if (!Array.isArray(r.squadsBuilt)) return 0;
  const v = r.squadsBuilt[0];
  return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}

function measureProgress(def: ChallengeDefinition, todayRows: readonly MatchTelemetryRow[]): number {
  switch (def.id) {
    case 'win-any':
      return todayRows.some((r) => viewerWonRow(r)) ? 1 : 0;
    case 'play-3':
      return Math.min(todayRows.length, 3);
    case 'play-5':
      return Math.min(todayRows.length, 5);
    case 'win-vanguard':
      return todayRows.some((r) => viewerWonRow(r) && viewerDoctrine(r) === 'vanguard') ? 1 : 0;
    case 'win-saboteur':
      return todayRows.some((r) => viewerWonRow(r) && viewerDoctrine(r) === 'saboteur') ? 1 : 0;
    case 'win-bulwark':
      return todayRows.some((r) => viewerWonRow(r) && viewerDoctrine(r) === 'bulwark') ? 1 : 0;
    case 'build-8-squads':
      return todayRows.some((r) => viewerSquadsBuilt(r) >= 8) ? 1 : 0;
    case 'win-fast':
      return todayRows.some((r) => viewerWonRow(r) && r.matchSeconds < 300) ? 1 : 0;
  }
}
